import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

const PILLARS = ["hook", "craft", "fit"];
const PILLAR_LABEL = { hook: "Hook", craft: "Craft", fit: "Fit" };
const BAND_COLORS = { HERO: "#16a34a", STRONG: "#2563eb", AVERAGE: "#d97706", RETHINK: "#dc2626" };

function Band({ band, big }) {
  if (!band || !band.label) return <span style={{ color: "var(--t3)" }}>—</span>;
  return <span style={{ fontSize: big ? 18 : 12, fontWeight: 800, color: BAND_COLORS[band.label] || "#666" }}>{band.emoji} {band.label}</span>;
}

/**
 * My Scorecard — the shared review the person themselves sees. Section A is
 * aggregate-only (no named voices); the role scorecard shows their own work.
 */
export default function MyScorecard() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/reviews/me");
        setRows(res.data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load scorecard");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="dm-page-container" style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
      <button onClick={() => navigate(-1)} style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--t3)", fontSize: 13, marginBottom: 16, background: "none", border: "none", cursor: "pointer" }}>
        <ArrowLeft size={16} /> BACK
      </button>
      <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 24 }}>My performance reviews</h1>

      {loading ? (
        <p style={{ color: "var(--t3)" }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: "var(--t3)" }}>No shared reviews yet.</p>
      ) : (
        rows.map((inst) => {
          const computed = inst.computed || {};
          const sectionB = computed.section_b_by_craft || {};
          const perCraft = computed.per_craft || {};
          return (
            <div key={inst.instance_id} style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: 20, marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 13, color: "var(--t3)" }}>Shared {inst.shared_at ? inst.shared_at.slice(0, 10) : ""}</div>
                  <div style={{ fontSize: 13, color: "var(--t3)" }}>Crafts: {(inst.crafts || []).join(", ")}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 30, fontWeight: 800 }}>{computed.composite != null ? computed.composite.toFixed(2) : "—"}</div>
                  <Band band={computed.band} big />
                </div>
              </div>
              {(inst.crafts || []).map((craft) => {
                const b = sectionB[craft] || {};
                const pc = perCraft[craft] || {};
                return (
                  <div key={craft} style={{ borderTop: "1px solid var(--bdr)", paddingTop: 12, marginTop: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <b style={{ textTransform: "capitalize" }}>{craft}</b>
                      <span style={{ fontSize: 13, color: "var(--t3)" }}>{b.item_count || 0} items · <Band band={pc.band} /></span>
                    </div>
                    <div style={{ display: "flex", gap: 24 }}>
                      {PILLARS.map((p) => (
                        <div key={p}>
                          <div style={{ fontSize: 11, color: "var(--t3)" }}>{PILLAR_LABEL[p]}</div>
                          <div style={{ fontSize: 18, fontWeight: 700 }}>{b.pillars?.[p] != null ? b.pillars[p].toFixed(1) : "—"}</div>
                        </div>
                      ))}
                      <div>
                        <div style={{ fontSize: 11, color: "var(--t3)" }}>Objective</div>
                        <div style={{ fontSize: 18, fontWeight: 700 }}>{b.objective_score != null ? b.objective_score.toFixed(1) : "—"}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })
      )}
    </div>
  );
}
